import { Title } from '@solidjs/meta'
import { type Component, createSignal } from 'solid-js'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Icon from '~/components/Icon'
import SidebarMenu from '~/components/SidebarMenu'
import type { SidebarMenuSectionType } from '~/types/SidebarMenu'

const sections: SidebarMenuSectionType[] = [
  {
    title: 'General',
    items: [
      { key: 'dashboard', label: 'Dashboard', icon: 'material-symbols-light:widgets-outline', href: '#dashboard' },
      { key: 'analytics', label: 'Analítica', icon: 'material-symbols-light:bar-chart', href: '#analytics', badge: 'New' },
      {
        key: 'ecommerce',
        label: 'E-commerce',
        icon: 'material-symbols-light:shopping-cart-outline',
        children: [
          { key: 'orders', label: 'Pedidos', href: '#orders', dotColor: 'green' },
          { key: 'products', label: 'Productos', href: '#products', dotColor: 'blue' },
          { key: 'invoices', label: 'Facturas', href: '#invoices', dotColor: 'orange' }
        ]
      }
    ]
  },
  {
    title: 'Gestión',
    items: [
      { key: 'users', label: 'Usuarios', icon: 'material-symbols-light:group-outline', href: '#users', badge: 12 },
      { key: 'settings', label: 'Configuración', icon: 'material-symbols-light:settings-outline', href: '#settings' }
    ]
  }
]

const SidebarMenuPage: Component = () => {
  const [active, setActive] = createSignal('#dashboard')
  const [collapsed, setCollapsed] = createSignal(false)
  const isActive = (href: string) => active() === href

  return (
    <>
      <Title>SidebarMenu - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6 text-balance">
          <h1 class="text-4xl font-bold text-foreground mb-4">SidebarMenu</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            Navegación lateral para paneles y dashboards. Soporta secciones, sub-ítems con indicadores de color, badges, estado activo, modo colapsado (solo iconos) y header/footer personalizados.
          </p>
        </div>

        <div class="space-y-12">
          {/* Interactivo */}
          <Card>
            <Card.Header
              title="Navegación interactiva"
              subtitle="Selecciona ítems y alterna el modo colapsado."
              icon={<Icon icon="material-symbols-light:left-panel-open-outline" class="text-primary" size={24} />}
            />
            <Card.Body>
              <div
                class="h-[460px] overflow-hidden rounded-2xl border border-border"
                onClick={e => {
                  const a = (e.target as HTMLElement).closest('a')
                  if (a) {
                    e.preventDefault()
                    const href = a.getAttribute('href')
                    if (href?.startsWith('#')) setActive(href)
                  }
                }}
              >
                <div class={collapsed() ? 'w-20 h-full' : 'w-72 h-full'}>
                  <SidebarMenu
                    sections={sections}
                    collapsed={collapsed()}
                    onToggleCollapse={() => setCollapsed(p => !p)}
                    isActive={isActive}
                    header={
                      <div class="flex items-center gap-2">
                        <div class="p-1.5 rounded-lg bg-primary text-white">
                          <Icon icon="material-symbols-light:widgets-outline" size={20} />
                        </div>
                        <span class="font-bold text-foreground">Panel</span>
                      </div>
                    }
                    footer={
                      <div class="flex items-center gap-3 text-sm">
                        <div class="h-8 w-8 rounded-full bg-surface-muted flex items-center justify-center">
                          <Icon icon="material-symbols-light:person-outline" size={18} class="text-foreground-subtle" />
                        </div>
                        <span class="text-foreground-subtle">Mi cuenta</span>
                      </div>
                    }
                  />
                </div>
              </div>
              <p class="mt-3 text-sm text-foreground-subtle">
                Ruta activa: <span class="font-mono text-primary">{active()}</span>
              </p>
            </Card.Body>
          </Card>

          {/* API Reference */}
          <Card>
            <Card.Header title="API Reference" subtitle="Propiedades del componente SidebarMenu." icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />} />
            <Card.Body>
              <div class="overflow-x-auto rounded-xl border border-border">
                <table class="w-full text-sm text-left">
                  <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                    <tr>
                      <th class="px-6 py-4 border-b border-border">Propiedad</th>
                      <th class="px-6 py-4 border-b border-border">Tipo</th>
                      <th class="px-6 py-4 border-b border-border">Descripción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-border">
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-primary">sections</td>
                      <td class="px-6 py-4 italic text-xs">{'{ title, items[] }[]'}</td>
                      <td class="px-6 py-4 italic">Secciones con sus ítems (icon, label, href, badge, children).</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">collapsed / onToggleCollapse</td>
                      <td class="px-6 py-4 italic text-xs">boolean / () =&gt; void</td>
                      <td class="px-6 py-4">Modo solo-iconos y su alternador.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-primary">isActive</td>
                      <td class="px-6 py-4 italic text-xs">(href) =&gt; boolean</td>
                      <td class="px-6 py-4 italic">Determina la ruta activa resaltada.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">header / footer</td>
                      <td class="px-6 py-4 italic text-xs">JSX.Element</td>
                      <td class="px-6 py-4">Contenido superior (marca) e inferior (cuenta).</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-primary">onNavigate</td>
                      <td class="px-6 py-4 italic text-xs">() =&gt; void</td>
                      <td class="px-6 py-4 italic">Callback al navegar (útil para cerrar el menú en móvil).</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}

export default SidebarMenuPage
